import { RefObject, useContext, useEffect, useMemo, useRef } from 'react';
import { useRouter } from 'next/router';

import { DateTime, getDateTime } from 'infras/datetime';
import { ProjectListItem } from 'presentations/api/schema/project';
import { CalendarSourceType } from 'presentations/api/schema/calendar_event/enum';
import { IntegrationMergedEventListItem } from 'presentations/api/schema/calendar_event/integrationMerged';
import { ViewerItem } from 'presentations/api/schema/user';
import { AccountCalendarListItem } from 'presentations/api/schema/account';

import { DashboardPortalContext } from 'presentations/reducers/DashboardPortal';
import { apiPath } from 'presentations/routes/path';

import {
  getEventTime,
  getIntegrationEventUrl,
  sortEventsByDate,
} from 'presentations/views/helpers/event';
import { useRequest } from 'presentations/views/hooks/request';
import { EventListItem } from 'presentations/api/schema/event';
import { eventDetailPath } from 'presentations/routes/path/event';
import { ProjectEventListItem } from 'presentations/api/schema/project/project_event';

interface Props {
  containerRef?: RefObject<HTMLElement>;
  viewer: ViewerItem;
  date: DateTime;
  today: DateTime;
  isToday: boolean;
  isFuture: boolean;
  setIsEventIntegrating?: Function;
  timeZone: string;
  events: ProjectEventListItem[];
  startedAt: number;
  endedAt: number;
}

export interface ViewModel {
  isAllDay: boolean;
  startedAt: number;
  startedAtString: string;
  endedAt: number;
  endedAtString: string;
  project?: ProjectListItem;
  event: IntegrationMergedEventListItem;
}

export const useDashboardEventList = (props: Props) => {
  const { state: portalState } = useContext(DashboardPortalContext);
  const router = useRouter();
  const currentRef = useRef<HTMLDivElement>(null);
  const { startedAt, endedAt } = props;

  useEffect(() => {
    if (!currentRef.current) return;
    currentRef.current.scrollIntoView({ block: 'nearest' });
  }, [currentRef.current]);

  const currentUserId = router.query.user
    ? String(router.query.user)
    : props.viewer.uid;

  const accountCalendarsRequest = useRequest(
    apiPath('/v1/calendars?isReadable=1'),
    {},
  );
  const accountCalendars: Array<AccountCalendarListItem> =
    accountCalendarsRequest.data || [];

  const events = props.events;
  const eventModels = useMemo(() => {
    const dateStart = props.date.startOf('day');
    const dateEnd = props.date.endOf('day');

    let viewModels: ViewModel[] = [];
    events.forEach((userEvent) => {
      userEvent.events.forEach((mergedEvent) => {
        // let date = getDateTime(mergedEvent.event.startedAt, props.timeZone);
        // const eventStartedAt = getDateTime(
        //   { year: date.year, month: date.month, day: date.day },
        //   props.timeZone,
        // );
        // date = getDateTime(mergedEvent.event.endedAt, props.timeZone);
        // const eventEndedAt = getDateTime(
        //   { year: date.year, month: date.month, day: date.day },
        //   props.timeZone,
        // );
        const current = mergedEvent.event;
        const originalStartedAt = getDateTime(
          current.startedAt,
          props.timeZone,
        );
        const originalEndedAt = getDateTime(current.endedAt, props.timeZone);
        let eventStartedAt: DateTime;
        let eventEndedAt: DateTime;
        if (current.isAllDay) {
          eventStartedAt = getDateTime(
            {
              year: originalStartedAt.year,
              month: originalStartedAt.month,
              day: originalStartedAt.day,
            },
            props.timeZone,
          );
          eventEndedAt = getDateTime(
            {
              year: originalEndedAt.year,
              month: originalEndedAt.month,
              day: originalEndedAt.day,
            },
            props.timeZone,
          );
        } else {
          eventStartedAt = originalStartedAt.plus({ hour: 0 }); // Duplicate
          eventEndedAt = originalEndedAt.plus({ hour: 0 }); // Duplicate
        }

        const isOverlap =
          startedAt < eventEndedAt.toMillis() &&
          endedAt > eventStartedAt.toMillis();
        if (!isOverlap) {
          return;
        }

        let project: ProjectListItem | undefined = undefined;
        if (mergedEvent.calendarSourceType === CalendarSourceType.Jicoo) {
          const event = mergedEvent.event as EventListItem;
          if (event.projectId !== null) {
            project = (portalState.projects || []).find(
              (p) => p.id === event.projectId,
            );
          }
        }
        viewModels.push({
          ...getEventTime(mergedEvent.event, dateStart, dateEnd),
          event: mergedEvent,
          project,
        });
      });
    });

    viewModels = viewModels.sort(sortEventsByDate);

    const current = props.today.valueOf();
    const alldayModels: ViewModel[] = [];
    const doneModels: ViewModel[] = [];
    const willModels: ViewModel[] = [];

    viewModels.forEach((m) => {
      if (m.isAllDay) {
        alldayModels.push(m);
      } else if (m.endedAt < current) {
        doneModels.push(m);
      } else {
        willModels.push(m);
      }
    });

    return {
      count: viewModels.length,
      alldayModels,
      doneModels,
      willModels,
      startedAt,
      endedAt,
    };
  }, [props.date, events, portalState.projects]);

  const onClickEvent = async (viewModel: ViewModel) => {
    const mergedEvent = viewModel.event;
    if (mergedEvent.calendarSourceType === CalendarSourceType.Jicoo) {
      const event = mergedEvent.event as EventListItem;
      router.push(eventDetailPath(event, router.asPath, props.viewer.uid));
    } else if (
      mergedEvent.calendarSourceType === CalendarSourceType.Integration
    ) {
      router.push(
        getIntegrationEventUrl(mergedEvent, props.viewer.uid, router.asPath),
      );
    }
  };

  return {
    state: {
      eventModels,
      accountCalendars,
      currentRef,
    },
    actions: {
      onClickEvent,
    },
  };
};
